import { errorSchema } from "@app/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ZodType } from "zod";
import { ApiError } from "../lib/api";
import {
  type ApercuReponse,
  type ApercuRequete,
  apercuReponseSchema,
  type FichierRendu,
  type FichiersReponse,
  type FichiersRequete,
  type FormatCadre,
  fichiersReponseSchema,
  REGLAGES_CADRE_PAR_DEFAUT,
  type ReglagesCadre,
  reglagesCadreSchema,
} from "./contract";
import type { ImageEnvoi } from "./silhouettes-rendu";

/**
 * L'envoi des apercus et des fichiers du module Cadres, et leur rythme (spec du 24 septembre
 * 2026, section 4 ; plan #167, tache B3). Le meme rythme que les silhouettes
 * (`silhouettes-rendu.ts`) : une nouvelle image part tout de suite, un reglage ou un ordre
 * 600 ms apres le dernier changement, **une seule requete en vol**, et une file d'une place ou
 * seule la derniere demande attend. `products` fait passer les calculs un par un (son verrou,
 * commun aux deux modules) : une rafale n'irait pas plus vite.
 *
 * Deux differences avec les silhouettes, propres aux cadres :
 *
 * - **L'ordre nomme une palette.** Change le nombre de couleurs, la fenetre (une fenetre ronde
 *   ne voit pas les coins de l'image) ou l'image, et la palette change avec : un ordre pose
 *   avant ne part plus, la requete porte `ordre: null` et l'outil repart de sa proposition.
 * - **Le nom ne refait pas l'apercu.** Il ne sert qu'aux noms des fichiers : le changer ne
 *   relance aucun calcul, et un nom invalide n'arrete pas l'apercu (la requete porte alors le
 *   nom par defaut, que l'apercu n'utilise pas). Ce sont les fichiers qui attendent un nom valide.
 */

export const ROUTE_APERCU = "/api/cadres/apercu";
export const ROUTE_FICHIERS = "/api/cadres/fichiers";

/** Le temps laisse a un reglage, ou a un ordre, pour se poser avant de partir. */
export const DELAI_REGLAGE_MS = 600;

/** Au-dela, la page dit que c'est plus long que d'habitude. */
export const DELAI_LONG_MS = 10_000;

/**
 * `POST` d'une requete du module, comme `apiGet` fait ses lectures : une `ApiError` avec le
 * code et le message du backend, `http_error` et le statut quand le corps n'est pas du JSON
 * (une coupure du frontal nginx), `contract_broken` pour une reponse hors contrat.
 */
async function poster<T>(
  route: string,
  requete: unknown,
  schema: ZodType<T>,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(route, {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify(requete),
    ...(signal ? { signal } : {}),
  });
  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = errorSchema.safeParse(payload);
    throw new ApiError(
      parsed.success ? parsed.data.error.code : "http_error",
      parsed.success ? parsed.data.error.message : `Erreur HTTP ${response.status}`,
      response.status,
    );
  }

  const result = schema.safeParse(payload);
  if (!result.success) {
    throw new ApiError("contract_broken", "Reponse hors contrat", response.status);
  }
  return result.data;
}

/** `POST /api/cadres/apercu`. */
export function envoyerApercu(
  requete: ApercuRequete,
  signal?: AbortSignal,
): Promise<ApercuReponse> {
  return poster(ROUTE_APERCU, requete, apercuReponseSchema, signal);
}

/** `POST /api/cadres/fichiers` : `format` manquant vaut `3mf`. */
export function envoyerFichiers(
  requete: FichiersRequete,
  signal?: AbortSignal,
): Promise<FichiersReponse> {
  return poster(ROUTE_FICHIERS, requete, fichiersReponseSchema, signal);
}

/** Une panne de `fetch` lui-meme (reseau coupe) : une erreur que la page sait lire. */
function erreurLisible(erreur: unknown): ApiError {
  return erreur instanceof ApiError
    ? erreur
    : new ApiError("reseau", "Le calcul est injoignable pour l'instant.", 0);
}

export type EtatApercu = "vide" | "attente" | "calcul" | "pret" | "erreur";

export type Apercu = {
  /**
   * `vide` sans image ; `attente` quand l'image est la mais que rien n'est demande pour elle
   * (un reglage invalide) ; `calcul` tant qu'une demande attend ou est en vol ; `pret` quand
   * l'apercu affiche repond a la derniere demande ; `erreur` quand celle-ci a echoue.
   */
  etat: EtatApercu;
  /** Le dernier apercu reussi **pour l'image courante**, garde (grise) pendant le calcul suivant. */
  apercu: ApercuReponse | null;
  /** La requete qui a produit `apercu` : les fichiers la reprennent, avec le nom du moment. */
  requete: ApercuRequete | null;
  /** L'echec de la derniere demande, et d'elle seule. */
  erreur: ApiError | null;
  /** Vrai apres 10 s de calcul. */
  long: boolean;
  /** Renvoie la derniere demande, telle quelle. */
  relancer: () => void;
};

type EtatMoteur = Exclude<EtatApercu, "attente">;

type Demande = { id: number; requete: ApercuRequete; image: ImageEnvoi };

type ApercuDeLImage = { apercu: ApercuReponse; requete: ApercuRequete; image: ImageEnvoi };

type ErreurDeLImage = { erreur: ApiError; image: ImageEnvoi };

type Afficheurs = {
  setEtat: (etat: EtatMoteur) => void;
  setApercu: (apercu: ApercuDeLImage) => void;
  setErreur: (erreur: ErreurDeLImage | null) => void;
  setLong: (long: boolean) => void;
};

/**
 * Le moteur du rythme, hors de React, sur le modele de celui des silhouettes : des minuteurs,
 * une requete en vol et une file d'une place, qui poussent leur etat dans les `useState` du
 * crochet. Il est cree une fois par montage.
 */
function creerMoteur(afficher: Afficheurs) {
  let actif = true;
  let compteur = 0;
  /** La derniere demande faite : en vol, ou en file derriere celle qui l'est. */
  let derniere: Demande | null = null;
  let issue: "attente" | "pret" | "erreur" = "attente";
  let enVol: { demande: Demande; controleur: AbortController } | null = null;
  let enFile: Demande | null = null;
  let minuteur: ReturnType<typeof setTimeout> | null = null;
  let minuteurLong: ReturnType<typeof setTimeout> | null = null;

  const rafraichir = (): void => {
    if (minuteur) afficher.setEtat("calcul");
    else if (!derniere) afficher.setEtat("vide");
    else if (enVol) afficher.setEtat("calcul");
    else afficher.setEtat(issue === "erreur" ? "erreur" : issue === "pret" ? "pret" : "calcul");
  };

  const annulerMinuteur = (): void => {
    if (minuteur) clearTimeout(minuteur);
    minuteur = null;
  };

  const envoyer = (demande: Demande): void => {
    const controleur = new AbortController();
    enVol = { demande, controleur };
    afficher.setLong(false);
    minuteurLong = setTimeout(() => afficher.setLong(true), DELAI_LONG_MS);
    rafraichir();
    envoyerApercu(demande.requete, controleur.signal)
      .then(
        (apercu) => {
          if (!actif) return;
          // Meme depasse, un apercu de la meme image s'affiche, grise : il est plus recent
          // que le precedent. Il ne rend la page « prete » que s'il repond a la derniere demande.
          afficher.setApercu({ apercu, requete: demande.requete, image: demande.image });
          if (derniere?.id === demande.id) {
            issue = "pret";
            afficher.setErreur(null);
          }
        },
        (erreur: unknown) => {
          if (!actif || controleur.signal.aborted) return;
          if (derniere?.id !== demande.id) return;
          issue = "erreur";
          afficher.setErreur({ erreur: erreurLisible(erreur), image: demande.image });
        },
      )
      .finally(() => {
        if (minuteurLong) clearTimeout(minuteurLong);
        minuteurLong = null;
        if (!actif || enVol?.demande.id !== demande.id) return;
        enVol = null;
        afficher.setLong(false);
        const suivante = enFile;
        enFile = null;
        if (suivante) envoyer(suivante);
        else rafraichir();
      });
  };

  const demander = (requete: ApercuRequete, image: ImageEnvoi): void => {
    compteur += 1;
    derniere = { id: compteur, requete, image };
    issue = "attente";
    afficher.setErreur(null);
    if (enVol) {
      enFile = derniere;
      rafraichir();
    } else {
      envoyer(derniere);
    }
  };

  return {
    planifier(requete: ApercuRequete, image: ImageEnvoi, delai: number): void {
      annulerMinuteur();
      if (delai <= 0) {
        demander(requete, image);
        return;
      }
      minuteur = setTimeout(() => {
        minuteur = null;
        demander(requete, image);
      }, delai);
      rafraichir();
    },
    /**
     * Un reglage invalide : rien ne part, le minuteur et la demande en file sont oublies. La
     * demande en vol ne redevient la derniere que si elle porte l'image affichee (voir
     * `CLAUDE.app.md`, module Silhouettes : ni le refus, ni la reponse, ni Reessayer d'une
     * autre image ne viennent sous celle-ci).
     */
    suspendre(image: ImageEnvoi): void {
      annulerMinuteur();
      if (enFile) {
        enFile = null;
        derniere = enVol?.demande ?? null;
        issue = "attente";
      }
      if (derniere && derniere.image !== image) {
        derniere = null;
        issue = "attente";
        afficher.setErreur(null);
      }
      rafraichir();
    },
    relancer(): void {
      if (!actif || enVol || !derniere) return;
      issue = "attente";
      envoyer(derniere);
    },
    reprendre(): void {
      actif = true;
    },
    arreter(): void {
      actif = false;
      annulerMinuteur();
      if (minuteurLong) clearTimeout(minuteurLong);
      minuteurLong = null;
      enVol?.controleur.abort();
      enVol = null;
      enFile = null;
    },
  };
}

/** L'etat que la page montre pour l'image affichee (meme regle que les silhouettes). */
function etatPourLImage(
  etat: EtatMoteur,
  image: ImageEnvoi | null,
  apercu: ApercuReponse | null,
  erreur: ApiError | null,
): EtatApercu {
  if (image === null) return "vide";
  if (etat === "vide") return "attente";
  if (etat === "pret" && apercu === null) return "attente";
  if (etat === "erreur" && erreur === null) return "attente";
  return etat;
}

/** Ce qui decide de la palette : l'image, le nombre de couleurs et la fenetre. */
type Palette = { image: ImageEnvoi | null; couleurs: ReglagesCadre["couleurs"]; fenetre: string };

/**
 * L'apercu de la page : `image` part tout de suite, un changement de reglage ou d'ordre 600 ms
 * apres le dernier, et rien ne part tant que les reglages sont hors bornes (une largeur en
 * cours de saisie). `ordre` ne part que pour la palette qu'il nommait (voir plus haut).
 */
export function useApercu(
  image: ImageEnvoi | null,
  reglages: ReglagesCadre,
  ordre: string[] | null,
): Apercu {
  const [etat, setEtat] = useState<EtatMoteur>("vide");
  const [apercu, setApercu] = useState<ApercuDeLImage | null>(null);
  const [erreur, setErreur] = useState<ErreurDeLImage | null>(null);
  const [long, setLong] = useState(false);
  const [moteur] = useState(() => creerMoteur({ setEtat, setApercu, setErreur, setLong }));
  const imageVue = useRef<ImageEnvoi | null>(null);

  // La palette que nommait l'ordre recu, notee a chaque nouvel ordre. Le ref est ecrit pendant
  // le rendu, mais toujours avec les memes valeurs pour les memes entrees : un double rendu
  // (mode strict) donne le meme resultat.
  const cleOrdre = JSON.stringify(ordre);
  const paletteDeLOrdre = useRef<{ cle: string; palette: Palette } | null>(null);
  const palette: Palette = { image, couleurs: reglages.couleurs, fenetre: reglages.fenetre };
  if (paletteDeLOrdre.current?.cle !== cleOrdre) {
    paletteDeLOrdre.current = { cle: cleOrdre, palette };
  }
  const nommee = paletteDeLOrdre.current.palette;
  const ordreValable =
    nommee.image === image &&
    nommee.couleurs === reglages.couleurs &&
    nommee.fenetre === reglages.fenetre
      ? ordre
      : null;

  // Le nom n'entre pas dans la cle : le changer ne refait pas l'apercu.
  const nom = reglagesCadreSchema.shape.nom.safeParse(reglages.nom).success
    ? reglages.nom
    : REGLAGES_CADRE_PAR_DEFAUT.nom;
  const pourLeCalcul = {
    largeur_mm: reglages.largeur_mm,
    fenetre: reglages.fenetre,
    couleurs: reglages.couleurs,
    epaisseur_mm: reglages.epaisseur_mm,
    ecart_mm: reglages.ecart_mm,
    led: reglages.led,
    ordre: ordreValable,
  };
  const cleCalcul = JSON.stringify(pourLeCalcul);
  const valides = reglagesCadreSchema.safeParse({ ...reglages, nom }).success;
  // Le nom du moment, relu par l'effet sans en etre une dependance.
  const nomDuMoment = useRef(nom);
  nomDuMoment.current = nom;

  useEffect(() => {
    moteur.reprendre();
    return () => moteur.arreter();
  }, [moteur]);

  useEffect(() => {
    if (!image) return;
    if (!valides) {
      moteur.suspendre(image);
      return;
    }
    const requete: ApercuRequete = {
      ...(JSON.parse(cleCalcul) as Omit<ApercuRequete, "nom" | "image" | "nom_fichier">),
      nom: nomDuMoment.current,
      image: image.base64,
      nom_fichier: image.nomFichier,
    };
    const nouvelle = imageVue.current !== image;
    imageVue.current = image;
    moteur.planifier(requete, image, nouvelle ? 0 : DELAI_REGLAGE_MS);
  }, [image, cleCalcul, valides, moteur]);

  const apercuVu = apercu && apercu.image === image ? apercu : null;
  const erreurVue = erreur && erreur.image === image ? erreur.erreur : null;
  return {
    etat: etatPourLImage(etat, image, apercuVu?.apercu ?? null, erreurVue),
    apercu: apercuVu?.apercu ?? null,
    requete: apercuVu?.requete ?? null,
    erreur: erreurVue,
    long,
    relancer: moteur.relancer,
  };
}

/** L'echec de la derniere fabrication, et le format demande, pour « Reessayer ». */
export type EchecFichiers = Pick<ApiError, "code" | "message" | "status"> & {
  format: FormatCadre;
};

export type Fichiers = {
  /** Fabrique le fichier puis le telecharge ; ne fait rien si une fabrication est en cours. */
  lancer: (requete: ApercuRequete, format: FormatCadre) => void;
  enCours: FormatCadre | null;
  erreur: EchecFichiers | null;
};

/** Les octets d'un fichier de la reponse, en `Blob`. */
export function blobDe(base64: string, type: string): Blob {
  const binaire = atob(base64);
  const octets = new Uint8Array(binaire.length);
  for (let i = 0; i < binaire.length; i += 1) octets[i] = binaire.charCodeAt(i);
  return new Blob([octets], { type });
}

/**
 * Les fichiers du cadre : une fabrication a la fois (3MF ou zip des STL, jusqu'a 15 s), puis
 * le telechargement, par un lien local (`Blob`) clique par la page. Le lien reste valable
 * jusqu'au telechargement suivant ou au demontage, ou il est revoque : un lien revoque tout de
 * suite apres le clic peut couper le telechargement dans certains navigateurs.
 *
 * `courante` est la requete de l'apercu affiche. **Un echec ne vaut que pour l'apercu sous
 * lequel il a ete lance** : une autre image ou d'autres reglages, donc un autre apercu, et il
 * n'est plus montre (il survivait au changement d'image, relecture de #176). Il est oublie par
 * comparaison d'identite, sans effet : aucune image ne montre l'echec d'un autre apercu.
 */
export function useFichiers(courante: ApercuRequete | null): Fichiers {
  const [enCours, setEnCours] = useState<FormatCadre | null>(null);
  const [erreur, setErreur] = useState<{
    echec: EchecFichiers;
    origine: ApercuRequete | null;
  } | null>(null);
  const enVol = useRef<AbortController | null>(null);
  const liens = useRef<string[]>([]);
  // La requete affichee au moment du clic, relue par `lancer` sans en changer l'identite.
  const affichee = useRef(courante);
  affichee.current = courante;

  useEffect(
    () => () => {
      enVol.current?.abort();
      enVol.current = null;
      for (const lien of liens.current) URL.revokeObjectURL(lien);
      liens.current = [];
    },
    [],
  );

  const telecharger = useCallback((fichiers: FichierRendu[]): void => {
    for (const lien of liens.current) URL.revokeObjectURL(lien);
    liens.current = [];
    for (const fichier of fichiers) {
      const lien = URL.createObjectURL(blobDe(fichier.contenu, fichier.type));
      liens.current.push(lien);
      const ancre = document.createElement("a");
      ancre.href = lien;
      ancre.download = fichier.nom;
      ancre.hidden = true;
      document.body.append(ancre);
      ancre.click();
      ancre.remove();
    }
  }, []);

  const lancer = useCallback(
    (requete: ApercuRequete, format: FormatCadre): void => {
      if (enVol.current) return;
      const controleur = new AbortController();
      const origine = affichee.current;
      enVol.current = controleur;
      setEnCours(format);
      setErreur(null);
      envoyerFichiers({ ...requete, format }, controleur.signal)
        .then(
          (reponse) => {
            if (controleur.signal.aborted) return;
            telecharger(reponse.fichiers);
          },
          (echec: unknown) => {
            if (controleur.signal.aborted) return;
            const lisible = erreurLisible(echec);
            setErreur({
              echec: {
                code: lisible.code,
                message: lisible.message,
                status: lisible.status,
                format,
              },
              origine,
            });
          },
        )
        .finally(() => {
          if (enVol.current !== controleur) return;
          enVol.current = null;
          setEnCours(null);
        });
    },
    [telecharger],
  );

  return {
    lancer,
    enCours,
    erreur: erreur !== null && erreur.origine === courante ? erreur.echec : null,
  };
}
